// NZ Harbour entrance data - safety-critical reference info
// Sources: LINZ NZ Pilot, Coastguard NZ, regional harbourmaster publications

export interface Harbour {
  name: string;
  region: string;
  island: 'North' | 'South';
  lat: number;
  lng: number;
  vhfChannel: number;
  barCrossing: boolean;
  entranceType: 'bar' | 'river' | 'harbour' | 'marina';
  description: string;
  hazards: string[];
  leadingLights: string;
  approach: string;
  tideNotes: string;
  coastguard: string;
  emergency: string;
}

export const HARBOURS: Harbour[] = [
  {
    name: 'Manukau Harbour',
    region: 'Auckland',
    island: 'North',
    lat: -37.03,
    lng: 174.49,
    vhfChannel: 18,
    barCrossing: true,
    entranceType: 'bar',
    description: 'Dangerous bar entrance on Auckland\'s west coast. One of NZ\'s most treacherous bar crossings. Only attempt in calm conditions with local knowledge.',
    hazards: ['Steep breaking waves on bar', 'Strong tidal currents (up to 6 knots)', 'Shifting channel', 'Heavy surf in swells > 1.5m', 'Very shallow at low tide'],
    leadingLights: 'Two beacons on Puhinui Ridge - align on 064°T for entrance channel',
    approach: 'From west, line up the leading lights. Cross the bar on the incoming tide when conditions are calm. Do NOT attempt in heavy swell or outgoing tide. Call Coastguard on VHF Ch 18 before crossing.',
    tideNotes: 'Best crossed 1-2 hours before high water on an incoming tide. Avoid low tide entirely - the bar can be dry or have dangerous breaking waves. Tidal range up to 4m.',
    coastguard: 'VHF Ch 18 - Manukau Coastguard',
    emergency: '*500 or 111',
  },
  {
    name: 'Kaipara Harbour',
    region: 'Auckland',
    island: 'North',
    lat: -36.39,
    lng: 174.16,
    vhfChannel: 18,
    barCrossing: true,
    entranceType: 'bar',
    description: 'NZ\'s largest harbour by area. The entrance is a wide, dangerous bar on the west coast. Extremely treacherous in any swell.',
    hazards: ['Massive bar with breaking waves up to 5m', 'Strong tidal currents', 'Shifting sand banks', 'Poor visibility in spray', 'No safe anchorage near entrance'],
    leadingLights: 'No fixed leading lights - use GPS and local knowledge. Channel markers are frequently repositioned.',
    approach: 'Extreme caution required. Only cross in calm conditions, incoming tide, daylight hours. The bar can break continuously across the entire entrance. Check with local fishermen before attempting.',
    tideNotes: 'Cross only near high water on an incoming tide. Tidal range up to 3m. Currents can exceed 6 knots on ebb.',
    coastguard: 'VHF Ch 18 - Coastguard Northern Region',
    emergency: '*500 or 111',
  },
  {
    name: 'Westhaven Marina',
    region: 'Auckland',
    island: 'North',
    lat: -36.84,
    lng: 174.76,
    vhfChannel: 77,
    barCrossing: false,
    entranceType: 'marina',
    description: 'NZ\'s largest marina, located in Auckland\'s Waitematā Harbour. No bar - sheltered harbour entrance. 1,800+ berths.',
    hazards: ['Heavy ferry traffic in harbour', 'Bridge clearance (harbour bridge spans)', 'Strong currents under bridge at peak tide'],
    leadingLights: 'No leading lights needed. Approach via Waitematā Harbour - well-marked channel.',
    approach: 'Enter from the Hauraki Gulf via Rangitoto Channel. Follow port hand marks. Westhaven is on the southern shore, west of the harbour bridge.',
    tideNotes: 'Minimal tidal effect in the marina. Harbour currents peak at ~2 knots in the channel under the bridge.',
    coastguard: 'VHF Ch 77 - Westhaven Marina control',
    emergency: '*500 or 111',
  },
  {
    name: 'Tauranga Harbour',
    region: 'Bay of Plenty',
    island: 'North',
    lat: -37.65,
    lng: 176.18,
    vhfChannel: 16,
    barCrossing: true,
    entranceType: 'bar',
    description: 'Matakana Island bar entrance. Popular recreational and commercial harbour. Bar can be dangerous in strong swells.',
    hazards: ['Bar at entrance between Mount Maunganui and Matakana Island', 'Breaking waves in swell', 'Strong tidal stream at entrance', 'Heavy commercial shipping traffic'],
    leadingLights: 'Leading beacons on Mount Maunganui - align for approach channel',
    approach: 'The entrance is well-marked with port and starboard buoys. Cross the bar in the marked channel. Watch for large ships - Tauranga is a major port. Monitor VHF Ch 16 for shipping movements.',
    tideNotes: 'Best crossed near high water. Ebb tide against swell creates dangerous conditions. Tidal range ~1.8m.',
    coastguard: 'VHF Ch 16 - Tauranga Harbour Control / Coastguard',
    emergency: '*500 or 111',
  },
  {
    name: 'Whangarei (Marsden Point)',
    region: 'Northland',
    island: 'North',
    lat: -35.84,
    lng: 174.47,
    vhfChannel: 12,
    barCrossing: false,
    entranceType: 'harbour',
    description: 'Whangarei Harbour entrance at Marsden Point. Deep water port - no bar. Sheltered but busy with commercial shipping (refinery, port).',
    hazards: ['Large vessel traffic (tankers, cargo ships)', 'Strong tidal currents at entrance', 'Marsden Point port security zone'],
    leadingLights: 'Leading beacons at Marsden Point for approach channel',
    approach: 'Well-marked deep water channel. Monitor VHF Ch 12 for port operations. Keep clear of commercial shipping - they have restricted manoeuvrability in the channel.',
    tideNotes: 'Tidal range ~2.5m. Currents up to 3 knots at the entrance.',
    coastguard: 'VHF Ch 12 - Marsden Point Harbour Control',
    emergency: '*500 or 111',
  },
  {
    name: 'Raglan Harbour',
    region: 'Waikato',
    island: 'North',
    lat: -37.80,
    lng: 174.87,
    vhfChannel: 18,
    barCrossing: true,
    entranceType: 'bar',
    description: 'West coast bar harbour. Popular for fishing and surfing. Bar can be dangerous - cross only in suitable conditions.',
    hazards: ['Bar breaks across entrance in swell', 'Narrow channel between training walls', 'Strong tidal currents', 'Shifting sand near entrance'],
    leadingLights: 'Training wall markers - follow the marked channel between the walls',
    approach: 'Cross between the training walls. The bar is immediately outside. Best on incoming tide, calm conditions. Do not attempt in heavy westerly swell.',
    tideNotes: 'Cross near high water on incoming tide. Tidal range ~2.5m. Ebb against swell = dangerous breaking waves.',
    coastguard: 'VHF Ch 18 - Coastguard Northern Region',
    emergency: '*500 or 111',
  },
  {
    name: 'Wellington Harbour',
    region: 'Wellington',
    island: 'North',
    lat: -41.29,
    lng: 174.78,
    vhfChannel: 14,
    barCrossing: false,
    entranceType: 'harbour',
    description: 'Large, deep, sheltered harbour. No bar. Home to CentrePort. Can experience strong winds - Wellington is famously windy.',
    hazards: ['Extreme wind conditions (gale force common)', 'Ferry traffic (Interislander & Bluebridge)', 'Squalls off surrounding hills', 'Chaffe Current at entrance'],
    leadingLights: 'Beacon at Pencarrow Head. Well-marked approach.',
    approach: 'Enter via Wellington Heads (Pencarrow Light). Large deep water harbour. Monitor VHF Ch 14 for port operations and ferry movements.',
    tideNotes: 'Small tidal range (~1.2m). Wind-driven water level changes can be larger than tidal. Currents at entrance up to 2 knots.',
    coastguard: 'VHF Ch 14 - Wellington Harbour Control / Coastguard',
    emergency: '*500 or 111',
  },
  {
    name: 'Picton / Queen Charlotte Sound',
    region: 'Marlborough',
    island: 'South',
    lat: -41.29,
    lng: 174.00,
    vhfChannel: 14,
    barCrossing: false,
    entranceType: 'harbour',
    description: 'Sheltered deep water entrance via Tory Channel. Interislander ferry terminal. One of NZ\'s safest harbour approaches.',
    hazards: ['Ferry traffic - ferries have priority in Tory Channel', 'Strong currents in Tory Channel (up to 5 knots)', 'Wakes from ferries'],
    leadingLights: 'Perano Head light at Tory Channel entrance. Multiple navigation aids throughout.',
    approach: 'Enter via Tory Channel (from Cook Strait) or Queen Charlotte Sound (from north). Tory Channel has strong tidal currents - transit at slack water if possible. Ferries have right of way.',
    tideNotes: 'Tidal range ~1.5m. Tory Channel currents can reach 5 knots on spring tides. Slack water is brief.',
    coastguard: 'VHF Ch 14 - Marlborough Harbour Control',
    emergency: '*500 or 111',
  },
  {
    name: 'Lyttelton Harbour',
    region: 'Canterbury',
    island: 'South',
    lat: -43.61,
    lng: 172.72,
    vhfChannel: 14,
    barCrossing: false,
    entranceType: 'harbour',
    description: 'Deep water volcanic harbour on Banks Peninsula. Major commercial port (Lyttelton Port). No bar - sheltered entrance.',
    hazards: ['Commercial shipping (container vessels, tankers)', 'Sumner Bar (inside harbour - small boats only)', 'Easterly swell through heads', 'Strong currents at entrance'],
    leadingLights: 'Godley Head light. Leading beacons for inner harbour.',
    approach: 'Enter between Godley Head and Adderley Head. Deep, well-marked channel. Monitor VHF Ch 14. Small boats should be aware of the Sumner Bar inside the harbour.',
    tideNotes: 'Tidal range ~2m. Currents at the heads up to 2-3 knots. Sumner Bar is dangerous at low water.',
    coastguard: 'VHF Ch 14 - Lyttelton Harbour Control / Coastguard',
    emergency: '*500 or 111',
  },
  {
    name: 'Akaroa Harbour',
    region: 'Canterbury',
    island: 'South',
    lat: -43.83,
    lng: 173.01,
    vhfChannel: 18,
    barCrossing: false,
    entranceType: 'harbour',
    description: 'Scenic volcanic harbour on Banks Peninsula. Popular tourist destination. Sheltered, deep water. No bar.',
    hazards: ['Narrow entrance with reef on southern side', 'Tourist boat traffic in season', 'Hector\'s dolphins (protected - keep speed down)'],
    leadingLights: 'Akaroa Head light. Reef beacon on southern side of entrance.',
    approach: 'Enter north of Akaroa Head. Avoid the reef on the southern side. Well-marked channel into the harbour.',
    tideNotes: 'Tidal range ~1.8m. Moderate currents at entrance.',
    coastguard: 'VHF Ch 18 - Coastguard Canterbury',
    emergency: '*500 or 111',
  },
  {
    name: 'Timaru Harbour',
    region: 'Canterbury',
    island: 'South',
    lat: -44.40,
    lng: 171.26,
    vhfChannel: 14,
    barCrossing: false,
    entranceType: 'harbour',
    description: 'Artificial harbour on Canterbury coast. No natural bar. Sheltered by breakwaters. Small craft harbour inside the main port.',
    hazards: ['Swell reflection off breakwaters in heavy seas', 'Commercial port traffic', 'Shifting shoals outside entrance'],
    leadingLights: 'Breakwater light. Leading beacons for approach.',
    approach: 'Approach from east, between the breakwaters. Well-marked entrance. Small craft use the inner harbour.',
    tideNotes: 'Tidal range ~2m. Minimal current inside harbour.',
    coastguard: 'VHF Ch 14 - Timaru Harbour Control',
    emergency: '*500 or 111',
  },
  {
    name: 'Otago Harbour (Port Chalmers)',
    region: 'Otago',
    island: 'South',
    lat: -45.82,
    lng: 170.62,
    vhfChannel: 12,
    barCrossing: false,
    entranceType: 'harbour',
    description: 'Long, narrow harbour from the entrance at Taiaroa Head to Dunedin. Port Chalmers is the deep water port. Sheltered - no bar.',
    hazards: ['Narrow channel - deep draft ships', 'Strong tidal currents in entrance and shipping channel', 'Taiaroa Head - strong ebb tide outflow', 'Yellow-eyed penguin area - speed restrictions'],
    leadingLights: 'Taiaroa Head light. Leading beacons through shipping channel.',
    approach: 'Enter via Taiaroa Head. Follow the marked shipping channel. Monitor VHB Ch 12. The harbour is ~21km long - allow time to transit to Dunedin marina.',
    tideNotes: 'Tidal range ~2m. Currents at Taiaroa Head up to 4 knots on ebb. Slack water window is short.',
    coastguard: 'VHF Ch 12 - Port Otago Control',
    emergency: '*500 or 111',
  },
  {
    name: 'Bluff Harbour',
    region: 'Southland',
    island: 'South',
    lat: -46.60,
    lng: 168.36,
    vhfChannel: 16,
    barCrossing: false,
    entranceType: 'harbour',
    description: 'Deep water harbour at NZ\'s southern tip. Commercial port (Tiwai Point aluminium smelter, fishing fleet). No bar - deep sheltered entrance.',
    hazards: ['Strong winds (Roaring Forties)', 'Commercial shipping', 'Foveaux Strait can be extremely rough'],
    leadingLights: 'Bluff light at Stirling Point. Leading beacons for approach.',
    approach: 'Enter from Foveaux Strait. Well-marked deep water approach. Monitor VHF Ch 16.',
    tideNotes: 'Tidal range ~2.5m. Strong currents in Foveaux Strait.',
    coastguard: 'VHF Ch 16 - Bluff Harbour Control / Coastguard Southland',
    emergency: '*500 or 111',
  },
  {
    name: 'Greymouth Harbour',
    region: 'West Coast',
    island: 'South',
    lat: -42.45,
    lng: 171.21,
    vhfChannel: 18,
    barCrossing: true,
    entranceType: 'bar',
    description: 'West coast river/bar harbour. Dangerous bar entrance. Only for experienced operators in suitable conditions.',
    hazards: ['Bar breaks across entire entrance in swell', 'Narrow training wall entrance', 'Strong river outflow on ebb', 'Heavy surf zone', 'Shifting sand'],
    leadingLights: 'Training wall markers - follow the marked channel',
    approach: 'Cross the bar only on incoming tide in calm conditions. The Grey River outflow meets the sea here - ebb tide against swell is extremely dangerous. Check with locals first.',
    tideNotes: 'Cross near high water on incoming tide. River flow affects the bar significantly. Tidal range ~2.5m.',
    coastguard: 'VHF Ch 18 - Coastguard West Coast',
    emergency: '*500 or 111',
  },
  {
    name: 'Westport Harbour',
    region: 'West Coast',
    island: 'South',
    lat: -41.75,
    lng: 171.61,
    vhfChannel: 18,
    barCrossing: true,
    entranceType: 'bar',
    description: 'Buller River entrance on the West Coast. Dangerous bar. Coal port and fishing harbour.',
    hazards: ['Bar at river mouth', 'Breaking waves in swell', 'Strong river current on ebb', 'Training wall - stay in marked channel'],
    leadingLights: 'Cape Foulwind light nearby. Training wall markers for entrance.',
    approach: 'Cross the bar on incoming tide, calm conditions. The Buller River is NZ\'s largest by flow - the outflow on ebb tide is very powerful. Do not attempt in westerly swell.',
    tideNotes: 'Cross near high water on incoming tide. River flow dominates the bar. Tidal range ~2.5m.',
    coastguard: 'VHF Ch 18 - Coastguard West Coast',
    emergency: '*500 or 111',
  },
  {
    name: 'Nelson Haven',
    region: 'Nelson',
    island: 'South',
    lat: -41.26,
    lng: 173.28,
    vhfChannel: 16,
    barCrossing: false,
    entranceType: 'harbour',
    description: 'Sheltered harbour entrance between Nelson and Tasman Bay. No bar. Popular recreational harbour. Large tidal flats.',
    hazards: ['Large area dries at low tide - stay in channel', 'Strong currents in entrance channel', 'Cutting (narrow entrance channel)'],
    leadingLights: 'Nelson Haven light. Leading beacons for The Cutting (entrance channel).',
    approach: 'Enter via The Cutting - a narrow marked channel. Stay within the channel - extensive tidal flats on either side. Approach at mid-to-high tide for small vessels.',
    tideNotes: 'Large tidal range (~3.5m). The Cutting dries at low water - deep draft vessels must enter near high tide.',
    coastguard: 'VHF Ch 16 - Coastguard Nelson',
    emergency: '*500 or 111',
  },
  {
    name: 'Whitianga Harbour',
    region: 'Coromandel',
    island: 'North',
    lat: -36.83,
    lng: 175.71,
    vhfChannel: 18,
    barCrossing: false,
    entranceType: 'harbour',
    description: 'Sheltered harbour on the Coromandel Peninsula. Popular for fishing and boating. No bar - well-protected entrance.',
    hazards: ['Shifting sandbanks inside harbour', 'Strong currents at entrance on spring tides', 'Heavy boat traffic in summer'],
    leadingLights: 'Entrance well-marked with buoys. Castle Rock visible as a navigation landmark.',
    approach: 'Enter via the marked channel. Watch for sandbanks inside the harbour, especially near the boat ramp. Popular fishing harbour - expect traffic in season.',
    tideNotes: 'Tidal range ~1.8m. Sandbanks shift - local knowledge helpful for shallow draft vessels at low tide.',
    coastguard: 'VHF Ch 18 - Coastguard Eastern Region',
    emergency: '*500 or 111',
  },
  {
    name: 'Whangarei (Town Basin)',
    region: 'Northland',
    island: 'North',
    lat: -35.73,
    lng: 174.32,
    vhfChannel: 77,
    barCrossing: false,
    entranceType: 'marina',
    description: 'Upper Whangarei Harbour - Town Basin marina. Reached via a 21km transit from Marsden Point entrance. Popular with cruisers.',
    hazards: ['Long transit from harbour entrance', 'Shallow areas at low tide in upper harbour', 'Oyster beds near channel edges'],
    leadingLights: 'Follow channel marks from Marsden Point. Town Basin is well-marked.',
    approach: 'Transit from Marsden Point entrance (see separate listing). Allow 1-1.5 hours. Channel is marked but shallow in places at low tide.',
    tideNotes: 'Tidal range ~2.5m. Upper harbour can be shallow - check depth for your draft.',
    coastguard: 'VHF Ch 77 - Town Basin Marina',
    emergency: '*500 or 111',
  },
  {
    name: 'Gisborne Harbour',
    region: 'Gisborne',
    island: 'North',
    lat: -38.66,
    lng: 178.02,
    vhfChannel: 16,
    barCrossing: false,
    entranceType: 'harbour',
    description: 'Open roadstead harbour on the East Coast. Artificial breakwater harbour. Can be exposed to easterly swell.',
    hazards: ['Exposed to easterly swell', 'Surge in harbour entrance', 'Commercial port traffic'],
    leadingLights: 'Breakwater light. Leading beacons for approach.',
    approach: 'Enter between the breakwaters. Well-marked entrance but can be uncomfortable in easterly conditions.',
    tideNotes: 'Tidal range ~1.5m. Surge can be significant in easterly swell.',
    coastguard: 'VHF Ch 16 - Gisborne Harbour Control',
    emergency: '*500 or 111',
  },
  {
    name: 'Napier Harbour',
    region: "Hawke's Bay",
    island: 'North',
    lat: -39.49,
    lng: 176.92,
    vhfChannel: 12,
    barCrossing: false,
    entranceType: 'harbour',
    description: 'Artificial harbour at Napier (Port of Napier). No bar - breakwater-protected entrance. Commercial port.',
    hazards: ['Commercial shipping (container, bulk cargo)', 'Easterly swell at entrance', 'Strong currents at Pania Reef'],
    leadingLights: 'Breakwater light. Leading beacons for approach channel.',
    approach: 'Enter via the marked channel between the breakwaters. Monitor VHF Ch 12. Stay clear of Pania Reef on the western side of approach.',
    tideNotes: 'Tidal range ~1.5m. Currents at entrance up to 2 knots.',
    coastguard: 'VHF Ch 12 - Port of Napier Control',
    emergency: '*500 or 111',
  },
  {
    name: 'New Plymouth Harbour',
    region: 'Taranaki',
    island: 'North',
    lat: -39.06,
    lng: 174.03,
    vhfChannel: 12,
    barCrossing: false,
    entranceType: 'harbour',
    description: 'Artificial breakwater harbour on the Taranaki coast. Exposed to westerly swell. Commercial port and fishing harbour.',
    hazards: ['Westerly swell at entrance', 'Surge inside harbour in heavy conditions', 'Commercial shipping', 'Sugar Loaf Islands - keep clear'],
    leadingLights: 'Breakwater light. Leading beacons for approach.',
    approach: 'Enter between the breakwaters. Well-marked entrance. Monitor VHF Ch 12. Can be uncomfortable in heavy westerly swell.',
    tideNotes: 'Tidal range ~2m. Surge can be significant. Wind-driven water level changes common.',
    coastguard: 'VHF Ch 12 - Port Taranaki Control',
    emergency: '*500 or 111',
  },
  {
    name: 'Russell / Bay of Islands',
    region: 'Far North',
    island: 'North',
    lat: -35.27,
    lng: 174.12,
    vhfChannel: 18,
    barCrossing: false,
    entranceType: 'harbour',
    description: 'Sheltered bay with multiple safe anchorages. One of NZ\'s finest cruising grounds. No bar - well-protected.',
    hazards: ['Heavy boat traffic in summer', 'Poihipi Rock (submerged, near Tapeka Point)', 'Strong currents in Veronica Channel'],
    leadingLights: 'No leading lights needed - large natural harbour with many landmarks. Tapeka Point and Russell wharf are key reference points.',
    approach: 'Enter via the Bay of Islands from the north or east. Well-marked with buoys and beacons. Many safe anchorages within the bay.',
    tideNotes: 'Tidal range ~2m. Currents up to 2 knots in Veronica Channel.',
    coastguard: 'VHF Ch 18 - Coastguard Bay of Islands',
    emergency: '*500 or 111',
  },
];

// VHF Radio Reference
export const VHF_INFO = {
  channels: [
    { channel: 16, purpose: 'Distress, Safety & Calling', notes: 'International distress, urgency and safety channel. Monitored by Coastguard and all vessels.' },
    { channel: 6, purpose: 'Intership Safety', notes: 'Common inter-ship channel for coordinated operations.' },
    { channel: 12, purpose: 'Port Operations', notes: 'Used by commercial ports for vessel traffic management.' },
    { channel: 14, purpose: 'Port Operations / Harbour Control', notes: 'Wellington, Lyttelton, Picton, Timaru harbour control.' },
    { channel: 18, purpose: 'Coastguard Regional', notes: 'Coastguard operating channel in many regions. Check local channel.' },
    { channel: 77, purpose: 'Marina Operations', notes: 'Marina berthing and control (Westhaven, Town Basin, etc.).' },
    { channel: 22, purpose: 'Coastguard Maritime Safety', notes: 'Coastguard maritime safety information broadcasts.' },
    { channel: 67, purpose: 'Supplementary Calling', notes: 'Used as supplementary calling channel in some regions.' },
  ],
  distressCalls: [
    {
      type: 'MAYDAY',
      when: ' Grave and imminent danger, immediate assistance required (sinking, fire, person overboard, life-threatening situation)',
      format: 'MAYDAY MAYDAY MAYDAY\nTHIS IS [vessel name] [vessel name] [vessel name]\nMAYDAY [vessel name]\nPOSITION: [lat/long or bearing/distance from landmark]\nNATURE OF DISTRESS: [e.g. sinking, fire]\nNUMBER OF PERSONS ON BOARD: [number]\nASSISTANCE REQUIRED: [what you need]\nOVER',
    },
    {
      type: 'PAN-PAN',
      when: ' Urgent situation but not immediately life-threatening (engine failure, medical issue, disoriented)',
      format: 'PAN-PAN PAN-PAN PAN-PAN\nTHIS IS [vessel name] [vessel name] [vessel name]\nPAN-PAN [vessel name]\nPOSITION: [position]\nPROBLEM: [nature of problem]\nASSISTANCE REQUIRED: [what you need]\nOVER',
    },
    {
      type: 'SÉCURITÉ',
      when: ' Safety announcement (navigation hazard, weather warning, abnormal vessel)',
      format: 'SÉCURITÉ SÉCURITÉ SÉCURITÉ\nALL STATIONS ALL STATIONS ALL STATIONS\nTHIS IS [vessel name]\nMESSAGE: [safety information]\nOUT',
    },
  ],
  emergencyContacts: [
    { name: 'Police / Ambulance / Fire', number: '111', notes: 'General emergency - will coordinate marine response' },
    { name: 'Coastguard NZ', number: '*500', notes: 'Free from Vodafone/Spark/2degrees mobile. For marine emergencies and assistance.' },
    { name: 'Coastguard VHF', channel: 'Channel 16', notes: 'Monitored 24/7 by Coastguard radio operators' },
    { name: 'Coastguard App', number: 'Coastguard NZ App', notes: 'Free app with logged trips, flares, VHF guide, and emergency call button' },
  ],
  rules: [
    { rule: 'Lifejackets', detail: 'Must be worn on vessels under 6m at all times. All vessels must carry appropriately sized lifejackets for everyone on board. Regional rules may require them to be worn in higher-risk situations.' },
    { rule: 'Speed Limits', detail: 'Maximum 5 knots within 200m of shore, any structure, or swimmers. Maximum 5 knots if any person is towed (unless an observer 10+ years old is present, then normal speed applies). Check regional bylaws for additional speed limits.' },
    { rule: 'Bar Crossings', detail: 'Coastguard recommends: wear lifejackets, cross on incoming tide, avoid ebb tide against swell, make a VHF call before crossing, don\'t follow in another vessel\'s wake (wait for them to clear the bar first).' },
    { rule: 'Alcohol', detail: 'The skipper must not be impaired by alcohol or drugs. Blood alcohol limit for skipper is the same as driving: 50mg/100ml (0.05). Passengers can consume alcohol but must not interfere with safe operation.' },
    { rule: 'Communications', detail: 'Carry at least two forms of communication: VHF radio (primary), cellphone in waterproof bag, EPIRB/PLB, flares. A VHF radio is the only device that lets rescue vessels find you by direction-finding.' },
    { rule: 'Weather', detail: 'Always check the marine forecast before departing. MetService marine forecasts on VHF, *500 mobile, or metservice.com/marine. Wind against tide creates dangerous conditions - especially on bars.' },
  ],
};

// Coastguard NZ Regional Radio Network
// Source: Coastguard NZ official VHF radio coverage
export const COASTGUARD_REGIONS = [
  {
    region: 'Northland & Bay of Islands',
    coastguardUnit: 'Bay of Islands Coastguard',
    vhfChannel: 18,
    coverage: 'Bay of Islands, Whangaroa, Cavalli Islands, east coast Northland',
    broadcastTimes: 'VHF Ch 22 - marine forecasts at 0533, 0733, 1033, 1333, 1633, 1933, 2233',
    repeaters: 'Kaitaia (Ch 63), Whangarei Heads (Ch 67), Bay of Islands (Ch 18)',
    contactNote: 'Call Coastguard on Ch 18 for trip reports, bar crossing reports, and assistance. Submit a trip report (Log) before departure and on arrival.',
  },
  {
    region: 'Auckland / Hauraki Gulf',
    coastguardUnit: 'Coastguard Northern Region',
    vhfChannel: 18,
    coverage: 'Hauraki Gulf, Auckland Harbour, Manukau Harbour, Firth of Thames, Coromandel Peninsula east coast',
    broadcastTimes: 'VHF Ch 22 - marine forecasts at 0533, 0733, 1033, 1333, 1633, 1933, 2233',
    repeaters: 'Waiheke Island (Ch 18), Kawau Island (Ch 18), Great Barrier Island (Ch 18), Manukau Heads (Ch 18)',
    contactNote: 'Coastguard Northern Region operates 24/7. Call on Ch 18 for trip reports, assistance, and bar crossing notifications. Manukau Bar crossing reports are mandatory - call before crossing.',
  },
  {
    region: 'Waikato / Raglan / West Coast',
    coastguardUnit: 'Raglan Coastguard',
    vhfChannel: 18,
    coverage: 'Raglan Harbour, Kawhia Harbour, Aotea Harbour, Port Waikato, west coast Waikato',
    broadcastTimes: 'VHF Ch 22 - marine forecasts at 0533, 0733, 1033, 1333, 1633, 1933, 2233',
    repeaters: 'Raglan (Ch 18), Kawhia (Ch 18)',
    contactNote: 'Call Coastguard on Ch 18 before crossing the Raglan or Kawhia bars. Bar crossing reports strongly recommended.',
  },
  {
    region: 'Bay of Plenty / Coromandel',
    coastguardUnit: 'Coastguard Eastern Region (Tauranga)',
    vhfChannel: 18,
    coverage: 'Tauranga Harbour, Bay of Plenty coast, Mercury Islands, Coromandel Peninsula east coast, Whitianga',
    broadcastTimes: 'VHF Ch 22 - marine forecasts at 0533, 0733, 1033, 1333, 1633, 1933, 2233',
    repeaters: 'Mount Maunganui (Ch 18), Whitianga (Ch 18), Whangamatā (Ch 18), Slipper Island (Ch 18)',
    contactNote: 'Call Coastguard on Ch 18 for Tauranga Bar crossing reports, trip reports, and assistance. Heavy commercial shipping traffic in Tauranga - monitor Ch 16.',
  },
  {
    region: 'East Coast / Gisborne / Hawke\'s Bay',
    coastguardUnit: 'Gisborne Coastguard / Napier Coastguard',
    vhfChannel: 16,
    coverage: 'East Cape, Gisborne, Mahia Peninsula, Napier, Hawke Bay',
    broadcastTimes: 'VHF Ch 22 - marine forecasts at 0533, 0733, 1033, 1333, 1633, 1933, 2233',
    repeaters: 'Gisborne (Ch 16), Mahia (Ch 67), Napier (Ch 67)',
    contactNote: 'Limited Coastguard coverage in remote areas. Carry EPIRB/PLB. Call on Ch 16 for assistance. VHF coverage can be patchy between Gisborne and Napier.',
  },
  {
    region: 'Taranaki',
    coastguardUnit: 'New Plymouth Coastguard',
    vhfChannel: 16,
    coverage: 'New Plymouth, Cape Egmont, Taranaki west coast',
    broadcastTimes: 'VHF Ch 22 - marine forecasts at 0533, 0733, 1033, 1333, 1633, 1933, 2233',
    repeaters: 'New Plymouth (Ch 67), Opunake (Ch 67)',
    contactNote: 'Call Coastguard on Ch 16 for trip reports and assistance. Exposed coastline - check conditions carefully.',
  },
  {
    region: 'Wellington / Cook Strait',
    coastguardUnit: 'Coastguard Wellington',
    vhfChannel: 14,
    coverage: 'Wellington Harbour, Cook Strait, Kapiti Coast, Wellington south coast',
    broadcastTimes: 'VHF Ch 22 - marine forecasts at 0533, 0733, 1033, 1333, 1633, 1933, 2233',
    repeaters: 'Wellington (Ch 14), Mana (Ch 67), Kapiti (Ch 67), Sinclair Head (Ch 67)',
    contactNote: 'Cook Strait is hazardous - strong currents, wind against tide, and ferry traffic. Call Coastguard on Ch 14 for trip reports before crossing Cook Strait. Monitor Ch 16 for ferry traffic.',
  },
  {
    region: 'Marlborough Sounds / Tasman Bay',
    coastguardUnit: 'Coastguard Marlborough',
    vhfChannel: 14,
    coverage: 'Queen Charlotte Sound, Tory Channel, Pelorus Sound, Tasman Bay, D\'Urville Island',
    broadcastTimes: 'VHF Ch 22 - marine forecasts at 0533, 0733, 1033, 1333, 1633, 1933, 2233',
    repeaters: 'Picton (Ch 14), Cape Jackson (Ch 67), Stephens Island (Ch 67), Nelson (Ch 67)',
    contactNote: 'Ferry traffic through Tory Channel - ferries have priority. Call Coastguard on Ch 14 for trip reports. Tory Channel currents can reach 5 knots - transit at slack water.',
  },
  {
    region: 'West Coast / South Island',
    coastguardUnit: 'Coastguard West Coast',
    vhfChannel: 18,
    coverage: 'Westport, Greymouth, Hokitika, Fiordland coast',
    broadcastTimes: 'VHF Ch 22 - marine forecasts at 0533, 0733, 1033, 1333, 1633, 1933, 2233',
    repeaters: 'Westport (Ch 18), Greymouth (Ch 18), Jackson Bay (Ch 67)',
    contactNote: 'Limited coverage in remote areas. Carry EPIRB/PLB. Bar crossings at Westport and Greymouth are dangerous - call Coastguard on Ch 18 before crossing.',
  },
  {
    region: 'Canterbury',
    coastguardUnit: 'Coastguard Canterbury',
    vhfChannel: 18,
    coverage: 'Lyttelton Harbour, Akaroa, Banks Peninsula, Canterbury coast',
    broadcastTimes: 'VHF Ch 22 - marine forecasts at 0533, 0733, 1033, 1333, 1633, 1933, 2233',
    repeaters: 'Lyttelton (Ch 18), Akaroa (Ch 18), Godley Head (Ch 67)',
    contactNote: 'Call Coastguard on Ch 18 for trip reports and assistance. Sumner Bar inside Lyttelton Harbour is dangerous - check conditions before crossing.',
  },
  {
    region: 'Otago',
    coastguardUnit: 'Coastguard Otago',
    vhfChannel: 16,
    coverage: 'Otago Harbour, Otago coast, Taieri River mouth, Moeraki',
    broadcastTimes: 'VHF Ch 22 - marine forecasts at 0533, 0733, 1033, 1333, 1633, 1933, 2233',
    repeaters: 'Taiaroa Head (Ch 67), Moeraki (Ch 67), Taieri Mouth (Ch 67)',
    contactNote: 'Call Coastguard on Ch 16 for trip reports. Otago Harbour entrance has strong currents - transit near slack water. Watch for wildlife (yellow-eyed penguins, sea lions).',
  },
  {
    region: 'Southland / Fiordland',
    coastguardUnit: 'Coastguard Southland',
    vhfChannel: 16,
    coverage: 'Bluff Harbour, Foveaux Strait, Stewart Island, Fiordland coast',
    broadcastTimes: 'VHF Ch 22 - marine forecasts at 0533, 0733, 1033, 1333, 1633, 1933, 2233',
    repeaters: 'Bluff (Ch 16), Stewart Island (Ch 67), Doubtful Sound (Ch 67), Milford Sound (Ch 67)',
    contactNote: 'Foveaux Strait can be extremely rough - check conditions. Fiordland is remote - carry EPIRB/PLB, satellite comms recommended. Limited VHF coverage in Fiordland sounds.',
  },
];

// Standard VHF call procedures for common boating situations
export const CALL_PROCEDURES = [
  {
    situation: 'Trip Report (Log)',
    icon: '📋',
    when: 'Before departing and on arrival - every trip',
    channel: 'Local Coastguard channel (usually Ch 18 or Ch 16)',
    steps: [
      'Switch to the local Coastguard working channel',
      'Call: "[Coastguard unit name], [Coastguard unit name], this is [your vessel name]"',
      'When they respond, give your trip report:',
      '  Vessel name and callsign (if you have one)',
      '  Number of persons on board',
      '  Departure point',
      '  Destination',
      '  Estimated time of arrival (ETA)',
      '  Vessel description (colour, type, length)',
      'On arrival, call Coastguard again to close your trip report ("[vessel name] safely arrived at [destination], closing log")',
    ],
    note: 'If you don\'t arrive by your ETA, Coastguard will start looking for you. Always close your trip report on arrival!',
  },
  {
    situation: 'Bar Crossing Report',
    icon: '🌊',
    when: 'Before crossing any bar - Manukau, Kaipara, Raglan, Tauranga, Greymouth, Westport',
    channel: 'Local Coastguard channel (Ch 18 for most bars)',
    steps: [
      'Call Coastguard 10-15 minutes before reaching the bar',
      'State your vessel name, position, and intention to cross',
      'Report number of persons on board',
      'Confirm you have assessed conditions and are proceeding',
      'After crossing safely, call Coastguard to confirm you\'re through',
      'If conditions are unsafe, call Coastguard and advise you\'re holding off',
    ],
    note: 'Coastguard will log your crossing. If you don\'t report safe passage, they will initiate a search. Wear lifejackets - all persons, no exceptions.',
  },
  {
    situation: 'Vessel Breakdown / Assistance',
    icon: '🔧',
    when: 'Engine failure, sail damage, or other non-life-threatening mechanical issue',
    channel: 'Local Coastguard channel. If no response, use Ch 16.',
    steps: [
      'Try your local Coastguard working channel first',
      'Call: "[Coastguard unit], this is [vessel name], requesting assistance"',
      'Give your position (GPS coordinates or bearing/distance from landmark)',
      'Describe the problem (engine failure, out of fuel, etc.)',
      'State number of persons on board',
      'Describe vessel (type, colour, length, distinctive features)',
      'If no response on working channel, switch to Ch 16 and call PAN-PAN',
    ],
    note: 'Coastguard will coordinate a tow or assistance vessel. If you have Coastguard membership, towing may be free. If conditions are deteriorating or you\'re drifting toward danger, escalate to PAN-PAN on Ch 16.',
  },
  {
    situation: 'Person Overboard',
    icon: '🔴',
    when: 'Someone has fallen overboard - immediate action required',
    channel: 'Ch 16 - MAYDAY if person is not recovered quickly',
    steps: [
      'SHOUT "MAN OVERBOARD!" and throw a lifebuoy or flotation device immediately',
      'Assign one crew member to keep visual contact with the person at all times - point continuously',
      'Turn the vessel ( Williamson turn or quick stop method ) to approach from downwind',
      'If person is not recovered within 2-3 minutes, call MAYDAY on Ch 16:',
      '  "MAYDAY MAYDAY MAYDAY, THIS IS [vessel name]"',
      '  "PERSON OVERBOARD, POSITION [lat/long or bearing from landmark]"',
      '  "NUMBER OF PERSONS ON BOARD: [revised number]"',
      '  "ASSISTANCE REQUIRED: IMMEDIATE"',
      'Continue recovery efforts - keep the person in sight',
    ],
    note: 'Time is critical in cold NZ waters. Hypothermia sets in within 10-30 minutes depending on water temp. If the person is recovered safely, cancel the MAYDAY by calling "CANCEL MAYDAY" on Ch 16.',
  },
  {
    situation: 'Medical Emergency',
    icon: '🏥',
    when: 'Crew member has a medical issue (injury, illness, chest pain, allergic reaction)',
    channel: 'Local Coastguard channel. If life-threatening, use Ch 16 PAN-PAN MEDICAL.',
    steps: [
      'Assess the patient - are they conscious, breathing, responsive?',
      'If life-threatening, call PAN-PAN on Ch 16:',
      '  "PAN-PAN PAN-PAN PAN-PAN, THIS IS [vessel name]"',
      '  "MEDICAL EMERGENCY"',
      '  "POSITION: [position]"',
      '  "NATURE OF EMERGENCY: [describe - e.g. chest pain, unconscious, severe bleeding]"',
      '  "NUMBER OF PERSONS ON BOARD: [number]"',
      'Coastguard will coordinate with Rescue Coordination Centre NZ (RCCNZ) and may dispatch helicopter or vessel',
      'Have patient\'s details ready: age, medications, allergies, what happened',
    ],
    note: 'If the patient is not breathing or has no pulse, start CPR immediately. Coastguard can connect you with a doctor via VHF or phone for medical advice while help is en route.',
  },
  {
    situation: 'Weather Check',
    icon: '🌦️',
    when: 'Before and during your trip - check conditions regularly',
    channel: 'VHF Ch 22 (Coastguard maritime safety broadcasts)',
    steps: [
      'Tune to Ch 22 at scheduled broadcast times (0533, 0733, 1033, 1333, 1633, 1933, 2233)',
      'Listen for the marine forecast for your area',
      'Note wind direction, speed, sea state, swell, and visibility',
      'Check for any navigational warnings (gale warnings, small craft advisories)',
      'For latest forecast between broadcasts, call Coastguard on the local working channel',
      'Alternatively: MetService marine forecast on *500 (mobile), metservice.com/marine, or the MetService Marine app',
    ],
    note: 'NZ weather changes fast. Check the forecast before departing AND while on the water. If conditions deteriorate, head to shelter early - don\'t wait. Wind against tide creates dangerous seas, especially on bars and in Cook Strait.',
  },
  {
    situation: 'Reporting a Hazard',
    icon: '⚠️',
    when: 'You see a navigation hazard (floating debris, unlit vessel, submerged object, erratic boat)',
    channel: 'Ch 16 - SÉCURITÉ call to all stations',
    steps: [
      'Switch to Ch 16',
      'Call: "SÉCURITÉ SÉCURITÉ SÉCURITÉ, ALL STATIONS, THIS IS [vessel name]"',
      'State the hazard: "NAVIGATION HAZARD - [describe: e.g. large floating log, submerged container, unlit vessel adrift]"',
      'Give position: "POSITION [lat/long or bearing/distance from landmark]"',
      'Give time observed: "OBSERVED AT [time] local"',
      'End with: "[vessel name] OUT"',
      'Also report to Coastguard on local working channel if practical',
    ],
    note: 'Reporting hazards could save a life. Floating debris is common after floods and storms, especially on the west coast and in Cook Strait. Logs and containers are particularly dangerous at night.',
  },
  {
    situation: 'Sinking / Flooding',
    icon: '🚨',
    when: 'Vessel is taking on water faster than bilge can handle - imminent sinking',
    channel: 'Ch 16 - MAYDAY immediately',
    steps: [
      'Trigger EPIRB immediately if you have one',
      'Get everyone into lifejackets NOW',
      'Call MAYDAY on Ch 16:',
      '  "MAYDAY MAYDAY MAYDAY, THIS IS [vessel name], [vessel name], [vessel name]"',
      '  "MAYDAY [vessel name]"',
      '  "POSITION: [lat/long or bearing from landmark]"',
      '  "NATURE OF DISTRESS: SINKING / TAKING ON WATER"',
      '  "NUMBER OF PERSONS ON BOARD: [number]"',
      '  "ASSISTANCE REQUIRED: IMMEDIATE - SINKING"',
      '  "OVER"',
      'Attempt to locate and plug the leak if safe to do so',
      'Prepare life raft or abandon ship gear',
      'If you must abandon ship, stay with the vessel if possible - it is easier to spot from the air',
    ],
    note: 'Do not abandon ship until the vessel is about to go under. A floating vessel (even partially submerged) is a larger radar target and visual marker than people in the water. Stay together. Activate EPIRB.',
  },
];
